import { useTranslation } from "react-i18next";
import { useState, useContext } from "react";
import { useLocation } from "react-router-dom";

import Header from "@/components/header/Header";
import Footer from "@/components/footer/Footer";
import ErrorMessage from "@/components/error-message/error-message";
import NeedHelpButton from "@/components/need-help-button/need-help-button";

import CallCenterSvg from "@/assets/images/call-center.svg";
import {
  ContactCenterRequest,
  ContactCenterOption,
} from "@/models/site-credentials/loyaltycard/ContactCenterRequest";
import { IdentifierValidationResponse } from "@/models/site-credentials/loyaltycard/IdentifierValidationResponse";
import { loyaltyCardService } from "@/services/site-credentials/loyaltycard/LoyaltyCardService";
import { DigitalAssetContext } from "@/contexts/site-credentials/DigitalAssetContext";
import { tryCatch } from "@/utils/try-catch";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import { Button } from "@/components/ui/button";
import ButtonContainer from "@/components/button-container";

export default function LoyaltyCardContactCenter() {
  const { t } = useTranslation();
  const location = useLocation<IdentifierValidationResponse>();
  const [loading, setLoading] = useState(false);
  const digitalAssetContext = useContext(DigitalAssetContext);
  const [error, setError] = useState("");

  const ANCHOR_NEED_HELP = "";
  const { handleResponse } = useApiResponseHandler();
  const nextStep = location?.state?.nextStep || "";

  const handleChoosedOption = async (
    contactCenterOption: ContactCenterOption
  ) => {
    setLoading(true);

    const contactCenterRequest: ContactCenterRequest = {
      contactCenterOption: contactCenterOption,
    };

    if (
      contactCenterRequest.contactCenterOption === ContactCenterOption.CALL &&
      digitalAssetContext.digitalAsset.isMobile === true
    ) {
      window.open("tel:+911111111", "_self");
    }

    const { data: response, error } = await tryCatch(loyaltyCardService.loyaltyCardCallCenterOption(
      contactCenterRequest,
      nextStep
    ));

    handleResponse(response, {
      setError,
    }, error);
    setLoading(false);
  };

  return (
    <div className="flex flex-col gap-12 pb-2">
      <Header
        title={t("call_center_loyalty_card.header_title")}
        subtitle={t("call_center_loyalty_card.header_message")}
        showLoading={loading}
      />
      <div className="justify-center self-center">
        <img src={CallCenterSvg} className="w-56 h-56" alt="call-center-loyalty-card" />
      </div>

      <ButtonContainer>
        <Button
          onClick={() => handleChoosedOption(ContactCenterOption.CALL)}
          aria-label={t("call_center_loyalty_card.button_call_center")}
          type="button"
          variant="secondary"
        >
          {t("call_center_loyalty_card.button_call_center")}
        </Button>
        <Button
          onClick={() => handleChoosedOption(ContactCenterOption.ANOTHER_LOYALTY_CARD)}
          aria-label={t("call_center_loyalty_card.another_card")}
          type="button"
          variant="secondary"
        >
          {t("call_center_loyalty_card.another_card")}
        </Button>
        <Button
          onClick={() => handleChoosedOption(ContactCenterOption.NEW_LOYALTY_CARD)}
          aria-label={t("call_center_loyalty_card.new_card")}
          type="button"
          variant="secondary"
        >
          {t("call_center_loyalty_card.new_card")}
        </Button>
        <Button
          onClick={() => handleChoosedOption(ContactCenterOption.HOME)}
          aria-label={t("call_center_loyalty_card.home")}
          type="button"
          variant="secondary"
        >
          {t("call_center_loyalty_card.home")}
        </Button>
      </ButtonContainer>
      <ErrorMessage errorString={error} />
      <NeedHelpButton anchor={ANCHOR_NEED_HELP} />
      <div
        role="note"
        aria-label={t("accessibility.signin.icons")}
        className="flex flex-col">
        <Footer />
      </div>
    </div>
  );
}
